// /////////////////////////////////////////////////////////////
// [ファイル] AgentGroupDetail.tsx
// [概要] エージェント設定ダイアログの右ペイン (グループの詳細)。旧 AssistantGroupDialog の
//        作成・編集・削除をここへ統合した。
//        - 閲覧: 用途、共有 (メンバー)、定義数、AI へ渡す文字数 (共通 + このグループ) と上限超過の案内
//        - 編集: グループ名・用途・個人/共有・共有メンバー・有効 (保存は親の SaveBar)
//        - 共通 ('') は編集・削除できない。定義が残るグループは削除できない (旧ダイアログと同じガード)
// /////////////////////////////////////////////////////////////
import React, { useEffect, useRef } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import * as Icons from '../../constants/icons';
import LoadingView from '../LoadingView';
import type { AgentGroup, AgentScope } from '../../hooks/useAssistantAgents';
import { DetailHeader, INJECTION_LIMIT, MetaRow, type StaffOption } from './agentSettingsShared';

export interface GroupEditForm {
  id?: string; // 更新時のみ
  scope: AgentScope;
  name: string;
  description: string;
  members: StaffOption[]; // scope='shared' の共有メンバー (作成者・本人は自動で含まれる)
  enabled: boolean;
}

interface Props {
  group: AgentGroup | null; // null = 共通 (または新規作成中)
  isCommon: boolean;
  form: GroupEditForm | null; // null = 閲覧
  onFormChange: (next: GroupEditForm) => void;
  saving: boolean;
  deleting: boolean;
  count: number; // このグループに属する定義数
  commonChars: number; // 共通の定義の注入文字数
  ownChars: number; // このグループの定義の注入文字数
  basePromptChars: number | null;
  staffOptions: StaffOption[];
  currentUserEmail: string;
  formatMembers: (emails: string[] | undefined) => string;
  resolve: (email: string) => string;
  narrow: boolean;
  onBack: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export default function AgentGroupDetail({
  group,
  isCommon,
  form,
  onFormChange,
  saving,
  deleting,
  count,
  commonChars,
  ownChars,
  basePromptChars,
  staffOptions,
  currentUserEmail,
  formatMembers,
  resolve,
  narrow,
  onBack,
  onEdit,
  onDelete,
}: Props) {
  const nameRef = useRef<HTMLInputElement>(null);
  const editing = form !== null;
  useEffect(() => {
    if (editing) {
      const t = window.setTimeout(() => nameRef.current?.focus({ preventScroll: true }), 80);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [editing]);

  if (saving) {
    return <LoadingView message={form?.id ? 'グループを更新しています...' : 'グループを作成しています...'} height="100%" size={40} />;
  }
  if (deleting) {
    return <LoadingView message="グループを削除しています..." height="100%" size={40} />;
  }

  const total = commonChars + ownChars;
  const warn = total > INJECTION_LIMIT;

  // --- 編集 ----------------------------------------------------------------
  if (form) {
    const set = <K extends keyof GroupEditForm>(key: K, value: GroupEditForm[K]) => onFormChange({ ...form, [key]: value });
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
        <DetailHeader
          narrow={narrow}
          onBack={onBack}
          kind={form.id ? 'グループを編集' : '新しいグループ'}
          title={
            <TextField
              inputRef={nameRef}
              size="small"
              fullWidth
              placeholder="グループ名 (例 会計分析、忘年会運営)"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              inputProps={{ maxLength: 40, 'aria-label': 'グループ名', style: { fontWeight: 700, fontSize: '0.95rem' } }}
              sx={{ mt: 0.25, '& .MuiInputBase-root': { bgcolor: '#fff' } }}
            />
          }
        />
        <TextField
          label="用途 (AI にこのグループの前提として示す 1〜2 文)"
          size="small"
          multiline
          minRows={2}
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
          inputProps={{ maxLength: 300 }}
          sx={{ '& .MuiInputBase-root': { bgcolor: '#fff' } }}
        />
        <MetaRow label="共有範囲">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={form.scope}
            onChange={(_, v) => {
              if (v) set('scope', v);
            }}
            sx={{ bgcolor: '#fff' }}
          >
            <ToggleButton value="personal" sx={{ textTransform: 'none', px: 1.5, py: 0.375, fontSize: '0.74rem' }}>
              <Icons.Person sx={{ fontSize: 15, mr: 0.5 }} /> 個人
            </ToggleButton>
            <ToggleButton value="shared" sx={{ textTransform: 'none', px: 1.5, py: 0.375, fontSize: '0.74rem' }}>
              <Icons.Groups sx={{ fontSize: 15, mr: 0.5 }} /> 共有
            </ToggleButton>
          </ToggleButtonGroup>
        </MetaRow>
        {form.scope === 'shared' && (
          <Autocomplete
            multiple
            size="small"
            options={staffOptions}
            value={form.members}
            onChange={(_, v) => set('members', v)}
            getOptionLabel={(o) => (o.name ? `${o.name} (${o.email})` : o.email)}
            isOptionEqualToValue={(a, b) => a.email === b.email}
            renderInput={(params) => (
              <TextField {...params} label="共有メンバー (自分は自動で含まれます)" sx={{ '& .MuiInputBase-root': { bgcolor: '#fff' } }} />
            )}
          />
        )}
        <MetaRow label="有効">
          <FormControlLabel
            control={<Switch size="small" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />}
            label={<Typography variant="caption">{form.enabled ? '有効' : '無効 (チャットのグループ選択に出ません)'}</Typography>}
            sx={{ ml: 0 }}
          />
        </MetaRow>
        <Alert severity="info" sx={{ fontSize: '0.76rem' }}>
          共有にすると、このグループに属する定義 ({count} 件) がメンバー全員に見えるようになります。変更した相手には Chat で通知されます。
        </Alert>
        <Box sx={{ height: 56 }} />
      </Box>
    );
  }

  // --- 閲覧 ----------------------------------------------------------------
  const name = isCommon ? '共通' : group?.name ?? '';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
      <DetailHeader
        narrow={narrow}
        onBack={onBack}
        kind="グループ"
        title={name}
        actions={
          isCommon ? undefined : (
            <>
              <Tooltip title="編集">
                <IconButton size="small" onClick={onEdit}>
                  <Icons.Edit sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={count > 0 ? '定義が残っているため削除できません (先に別グループか共通へ移してください)' : '削除'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={onDelete}
                    disabled={count > 0}
                    sx={{ '&:hover': { color: 'error.main', bgcolor: '#fdecea' } }}
                  >
                    <Icons.Delete sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )
        }
      />
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
        {isCommon ? '共通の定義はすべてのチャットで AI に渡されます。' : group?.description || '用途は未設定です'}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <MetaRow label="共有">
          {isCommon ? (
            <Typography variant="caption">全員 (定義ごとの共有設定に従う)</Typography>
          ) : group?.scope === 'shared' ? (
            <>
              <Chip size="small" icon={<Icons.Groups />} label={`共有 ${group.shared_with.length}人`} sx={{ fontSize: '0.68rem', height: 22, '& .MuiChip-icon': { fontSize: 13 } }} />
              <Typography variant="caption">{formatMembers(group.shared_with)}</Typography>
            </>
          ) : (
            <>
              <Chip size="small" icon={<Icons.Person />} label="個人" sx={{ fontSize: '0.68rem', height: 22, '& .MuiChip-icon': { fontSize: 13 } }} />
              <Typography variant="caption">自分だけ</Typography>
            </>
          )}
        </MetaRow>
        {group && group.user_id !== currentUserEmail && (
          <MetaRow label="作成者">
            <Typography variant="caption">{resolve(group.user_id)}</Typography>
          </MetaRow>
        )}
        {group && !group.enabled && (
          <MetaRow label="状態">
            <Chip size="small" label="無効" sx={{ fontSize: '0.68rem', height: 22 }} />
            <Typography variant="caption" color="text.secondary">
              チャットのグループ選択に出ません
            </Typography>
          </MetaRow>
        )}
        <MetaRow label="定義数">
          <Typography variant="caption">{count} 件 (左の一覧)</Typography>
        </MetaRow>
        <MetaRow label="AI へ渡す文字数">
          <Typography variant="body2" sx={{ fontWeight: 700, color: warn ? 'warning.main' : 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
            {total.toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            / {INJECTION_LIMIT.toLocaleString()} 字{' '}
            {isCommon ? '(グループを選ばないチャットで渡る量)' : `(共通 ${commonChars.toLocaleString()} + このグループ ${ownChars.toLocaleString()})`}
          </Typography>
          {warn && (
            <Typography variant="caption" sx={{ color: 'warning.main', width: '100%' }}>
              上限超過。回答精度が落ちるため定義を減らすか分割してください
            </Typography>
          )}
          {basePromptChars !== null && (
            <Typography variant="caption" color="text.secondary" sx={{ width: '100%', fontSize: '0.66rem' }}>
              送信合計の目安 基本 {basePromptChars.toLocaleString()} + 共通 {commonChars.toLocaleString()}
              {isCommon ? '' : ` + このグループ ${ownChars.toLocaleString()}`} = {(basePromptChars + total).toLocaleString()} 字
            </Typography>
          )}
        </MetaRow>
      </Box>
      <Alert severity="info" sx={{ fontSize: '0.76rem' }}>
        グループは会話の「作業文脈」の単位です。グループ付きの定義は、グループの共有先をそのまま引き継ぎます。
        定義が残っているグループは削除できません (先に別グループか共通へ移してください)。
      </Alert>
    </Box>
  );
}
